import React from 'react';
import { PhoneIcon, CalendarIcon, XCircleIcon, ClockIcon, FileTextIcon, ZapIcon, BellRingIcon } from 'lucide-react';
import { AnyContact, Lead, Loan, Retention } from '../types';
interface ContactCardProps {
  contact: AnyContact;
  isActive: boolean;
  onClick: (contact: AnyContact) => void;
  onCall: (contact: AnyContact) => void;
  onSchedule?: (contact: AnyContact) => void;
  onRemove?: (contact: AnyContact) => void;
  removeLabel?: string;
}
export function ContactCard({
  contact,
  isActive,
  onClick,
  onCall,
  onSchedule,
  onRemove,
  removeLabel
}: ContactCardProps) {
  const today = new Date().toISOString().split('T')[0];
  const isFollowUpToday = contact.scheduledFollowUp === today;
  const borderStyle = isActive ? 'bg-olympian-gold/5 border-olympian-gold/30 shadow-[inset_4px_0_0_rgba(134,194,138,1)]' : isFollowUpToday ? 'bg-emerald-500/5 border-emerald-500/30 shadow-[inset_4px_0_0_rgba(16,185,129,0.8)] hover:bg-emerald-500/10' : 'bg-olympian-surface/50 border-olympian-border hover:bg-olympian-surface hover:border-olympian-textMuted/30';
  const renderLeadContent = (lead: Lead) => {
    const latestNote = lead.history.find(h => h.type === 'note') || lead.history[0];
    return <>
        <div className="col-span-4 flex items-center gap-3 min-w-0" data-id="element-39">
          <Avatar name={lead.name} data-id="element-40" />
          <div className="min-w-0" data-id="element-41">
            <div className="font-semibold text-theme text-sm truncate" data-id="element-42">
              {lead.name}
            </div>
            <div className="text-xs text-olympian-textMuted font-mono" data-id="element-43">
              {lead.phone}
            </div>
          </div>
        </div>
        <div className="col-span-2 flex flex-col gap-1" data-id="element-44">
          <div className="flex items-center gap-1.5 text-xs text-olympian-textMuted" data-id="element-45">
            <ZapIcon className="w-3 h-3 shrink-0" data-id="element-46" />
            <span className="truncate" data-id="element-47">{lead.source}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-olympian-textMuted" data-id="element-48">
            <ClockIcon className="w-3 h-3 shrink-0" data-id="element-49" />
            <span data-id="element-50">{lead.daysSinceContact}d ago</span>
          </div>
        </div>
        <div className="col-span-4 min-w-0" data-id="element-51">
          <div className="text-xs text-olympian-textMuted italic truncate leading-relaxed" title={latestNote?.description} data-id="element-52">
            &ldquo;{latestNote?.description}&rdquo;
          </div>
        </div>
      </>;
  };
  const renderLoanContent = (loan: Loan) => {
    const progress = loan.docsReceived / loan.docsTotal * 100;
    return <>
        <div className="col-span-4 flex items-center gap-3 min-w-0" data-id="element-53">
          <Avatar name={loan.name} data-id="element-54" />
          <div className="min-w-0" data-id="element-55">
            <div className="font-semibold text-theme text-sm truncate" data-id="element-56">
              {loan.name}
            </div>
            <div className="text-xs text-olympian-textMuted font-mono" data-id="element-57">
              {loan.phone}
            </div>
          </div>
        </div>
        <div className="col-span-2 flex flex-col gap-1" data-id="element-58">
          <div className="text-sm font-semibold text-emerald-400 font-mono" data-id="element-59">
            ${(loan.amount / 1000).toFixed(0)}k
          </div>
          <div className="text-xs text-olympian-textMuted truncate" data-id="element-60">
            {loan.loanType} • {loan.daysInPipeline}d
          </div>
        </div>
        <div className="col-span-4 pr-2" data-id="element-61">
          <div className="flex justify-between text-xs mb-1.5" data-id="element-62">
            <span className="text-olympian-textMuted flex items-center gap-1" data-id="element-63">
              <FileTextIcon className="w-3 h-3" data-id="element-64" /> Docs
            </span>
            <span className="font-medium text-theme font-mono" data-id="element-65">
              {loan.docsReceived}/{loan.docsTotal}
            </span>
          </div>
          <div className="w-full bg-olympian-navyLight rounded-full h-1.5 overflow-hidden" data-id="element-66">
            <div className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500" style={{
            width: `${progress}%`
          }} data-id="element-67" />
          </div>
        </div>
      </>;
  };
  const renderRetentionContent = (retention: Retention) => {
    return <>
        <div className="col-span-4 flex items-center gap-3 min-w-0" data-id="element-68">
          <Avatar name={retention.name} data-id="element-69" />
          <div className="min-w-0" data-id="element-70">
            <div className="font-semibold text-theme text-sm truncate" data-id="element-71">
              {retention.name}
            </div>
            <div className="text-xs text-olympian-textMuted font-mono" data-id="element-72">
              {retention.phone}
            </div>
          </div>
        </div>
        <div className="col-span-2 flex flex-col gap-1" data-id="element-73">
          <div className="text-sm font-semibold text-olympian-gold font-mono" data-id="element-74">
            ${(retention.amount / 1000).toFixed(0)}k
          </div>
          <div className="flex items-center gap-1.5 text-xs text-olympian-textMuted" data-id="element-75">
            <CalendarIcon className="w-3 h-3 shrink-0" data-id="element-76" />
            <span data-id="element-77">
              {new Date(retention.originalLoanDate).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="col-span-4 min-w-0" data-id="element-78">
          <div className="bg-olympian-navyLight text-olympian-textMuted text-xs px-2.5 py-1.5 rounded-md border border-olympian-border truncate" title={retention.triggerReason} data-id="element-79">
            <span className="text-theme font-medium" data-id="element-80">Trigger:</span>{' '}
            {retention.triggerReason}
          </div>
        </div>
      </>;
  };
  return <div className={`w-full text-left p-4 rounded-lg border transition-all duration-200 cursor-pointer group relative ${borderStyle}`} onClick={() => onClick(contact)} data-id="element-81">
      {/* Follow-up today indicator */}
      {isFollowUpToday && <div className="flex items-center gap-1.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3 w-fit" data-id="element-82">
          <BellRingIcon className="w-3 h-3" data-id="element-83" />
          Follow-up today
        </div>}

      {/* Main content row */}
      <div className="flex items-center gap-2" data-id="element-84">
        <div className="flex-1 grid grid-cols-10 gap-4 items-center min-w-0" data-id="element-85">
          {contact.type === 'lead' && renderLeadContent(contact as Lead)}
          {contact.type === 'loan' && renderLoanContent(contact as Loan)}
          {contact.type === 'retention' && renderRetentionContent(contact as Retention)}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 shrink-0 pl-2" onClick={e => e.stopPropagation()} data-id="element-86">
          <button onClick={() => onCall(contact)} className="bg-olympian-gold text-olympian-navy hover:bg-olympian-goldHover p-2.5 rounded-md transition-colors shadow-[0_0_12px_rgba(134,194,138,0.35)]" title="Call" aria-label={`Call ${contact.name}`} data-id="element-87">
            <PhoneIcon className="w-4 h-4" data-id="element-88" />
          </button>
          {onSchedule && <button onClick={() => onSchedule(contact)} className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/25 hover:border-emerald-400 p-2.5 rounded-md transition-colors" title="Schedule Follow-up" aria-label={`Schedule follow-up for ${contact.name}`} data-id="element-89">
              <CalendarIcon className="w-4 h-4" data-id="element-90" />
            </button>}
          {onRemove && <button onClick={() => onRemove(contact)} className="bg-red-500/15 text-red-400 border border-red-500/40 hover:bg-red-500/25 hover:border-red-400 p-2.5 rounded-md transition-colors" title={removeLabel || 'Remove'} aria-label={`${removeLabel || 'Remove'} ${contact.name}`} data-id="element-91">
              <XCircleIcon className="w-4 h-4" data-id="element-92" />
            </button>}
        </div>
      </div>
    </div>;
}
function Avatar({
  name
}: {
  name: string;
}) {
  return <div className="w-9 h-9 rounded-full bg-olympian-surface border border-olympian-border flex items-center justify-center text-olympian-gold font-bold text-sm shrink-0" data-id="element-93">
      {name.charAt(0)}
    </div>;
}